import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import { Card, CardContent, CardHeader } from '@/components';
import {
  CardDescription,
  CardFooter,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/atoms';

export const MonthlyVisitorsChart: FunctionComponent<{
  monthlyVisits: Record<string, number> | undefined;
  trend: { direction: string; percentage: number };
}> = ({ monthlyVisits, trend }) => {
  const dateRange = useMemo(() => {
    const months = Object.keys(monthlyVisits ?? {});

    if (months.length === 0) return 'last 6 months';

    const sortedMonths = months.sort();
    const startDate = dayjs(sortedMonths[0]).format('MMM YYYY');
    const endDate = dayjs(sortedMonths[sortedMonths.length - 1]).format('MMM YYYY');

    return `${startDate} - ${endDate}`;
  }, [monthlyVisits]);
  let minVisitors = 0;
  let maxVisitors = 0;

  Object.values(monthlyVisits ?? {}).forEach(num => {
    if (num < minVisitors) {
      minVisitors = num;
    }

    if (num > maxVisitors) {
      maxVisitors = num;
    }
  });

  const visitorsTotalArea = maxVisitors - minVisitors;

  const isEmpty = Object.entries(monthlyVisits ?? {}).length === 0;

  if (isEmpty) {
    return (
      <Card className="w-full 2xl:w-3/5">
        <CardHeader className="px-6 pb-2 pt-4 font-bold">Estimated Monthly Visitors</CardHeader>
        <CardContent className="px-6 pb-4 pt-0">
          <p className="text-sm text-slate-500">No monthly visitors data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex h-[30rem] w-full flex-col 2xl:h-auto 2xl:w-3/5">
      <CardHeader className="px-6 pb-2 pt-4 font-bold">
        Estimated Monthly Visitors
        <CardDescription className="text-muted-foreground text-sm font-normal">
          Showing total visitors for {dateRange}
        </CardDescription>
      </CardHeader>

      <CardContent className="relative h-full p-2">
        <ChartContainer
          className="h-[20rem] w-[95%] 2xl:w-full"
          config={{
            visitors: {
              label: 'Visited',
              color: '#007aff',
            },
          }}
        >
          <AreaChart
            accessibilityLayer
            data={Object.entries(monthlyVisits ?? {}).map(([month, value]) => ({
              month,
              visitors: value,
            }))}
            margin={{
              left: 12,
              right: 12,
            }}
          >
            <defs>
              <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#007aff" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#007aff" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={value => dayjs(value).format('MMM YYYY')}
            />
            <YAxis
              ticks={[
                minVisitors,
                Math.trunc(visitorsTotalArea / 4),
                Math.trunc(visitorsTotalArea / 2),
                Math.trunc((3 * visitorsTotalArea) / 4),
                maxVisitors,
              ]}
              domain={[minVisitors - (maxVisitors * 1.2 - maxVisitors), maxVisitors * 1.2]}
              tickFormatter={value =>
                Intl.NumberFormat('en', { notation: 'compact' }).format(value)
              }
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  indicator="dot"
                  valueRender={value => (
                    <span className="text-foreground ml-4 font-mono font-medium tabular-nums">
                      {Intl.NumberFormat('en').format(Number(value))}
                    </span>
                  )}
                />
              }
            />
            <Area
              dataKey="visitors"
              type="natural"
              fill="url(#colorVisitors)"
              fillOpacity={0.4}
              stroke="#007aff"
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
      <CardFooter>
        <div className="flex w-full items-start gap-2 text-sm">
          <div className="grid gap-2">
            <div className="flex items-center gap-2 font-medium leading-none">
              {trend.direction !== 'No trend data' && (
                <>
                  {trend.direction === 'up' ? (
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500" />
                  )}
                  <span>{`Trending ${trend.direction} by ${trend.percentage.toFixed(
                    1,
                  )}% this month`}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};
